import React from 'react';

import * as analytics from 'analytics/Analytics';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import {InaccessibleInfo, isInaccessibleInfo} from 'auth/RequestAccess';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ContainerSummary,
  GridSummaryGraphQL,
  GridType,
  PlateSummaryExtras,
  StorableSummary,
  StorableSummaryExtras,
} from 'constants/SampleConstants';
import {MinimalExpirationInfo, MinimalFreezeThawInfo} from 'constants/SampleStatusConstants';
import PrintLabelButton from 'label-printing/PrintLabelButton';
import {ListingPagination} from 'lib/Listings';
import navigateToURL from 'lib/navigateToURL';
import TagValueViewer from 'metadata/TagValueViewer';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {getInventorySearchUrlForContainable} from 'samples/containables/ContainableUtil';
import ContainerContentConcentration from 'samples/containables/ContainerContentConcentration';
import {useContainerValidContentsQuery} from 'samples/containers/ValidContentsUtils';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import * as SampleUtil from 'samples/SampleUtil';
import * as StorableActions from 'samples/StorableActions';
import {restStorableSummaryToGraphQL} from 'samples/storables/graphql/translateStorable';
import * as ParentStorableUtil from 'samples/storables/ParentStorableUtil';
import StorableStore from 'samples/StorableStore';
import {Measurement} from 'samples/transfers/Measurements';
import ExpirationDateCell from 'samples/valid-contents/ExpirationDateCell';
import FreezeThawCountCell from 'samples/valid-contents/FreezeThawCountCell';
import {getStorableTagColumnsData} from 'schemas/tags/TableTagUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonStyle} from 'ui-library/buttons/Button';
import LinkCell from 'ui-library/simple-table/LinkCell';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import APIUtil from 'util/APIUtil';
import {readableTime} from 'util/DateUtil';

import {AncestralStorables} from './AncestralStorables';

function isAccessible(storable: StorableSummary | InaccessibleInfo): storable is StorableSummary {
  return !isInaccessibleInfo(storable);
}

function isContainer(storable: StorableSummary): storable is ContainerSummary {
  return APIUtil.apiIdToType(storable.id) === ModelTypeConstants.CONTAINER;
}

function getTableMessage(totalNumWithHidden: number, totalNum: number, itemWord: string): JSX.Element {
  const numImpermissibleRows = totalNumWithHidden - totalNum;
  return (
    <span>
      {numImpermissibleRows > 0 && (
        <span className="u-bold-font-weight">{numImpermissibleRows} hidden. </span>
      )}
      {totalNumWithHidden} total {pluralize(totalNumWithHidden, itemWord)}.
    </span>
  );
}

function getStorableTagColumns(storables: Array<StorableSummary>): Array<JSX.Element> {
  return getStorableTagColumnsData(storables).map(({name}) => (
    <Column
      key={name}
      header={name}
      renderCell={(rowIndex) => {
        const tag = storables[rowIndex].tags.find((tag) => tag.name === name);
        return <Cell>{tag && <TagValueViewer tag={tag} />}</Cell>;
      }}
    />
  ));
}

interface EnhancedProps {
  storableOrInaccessibleInfoById: {
    [id: string]: StorableSummary | InaccessibleInfo;
  };
}

class StorableInfoTableConnector extends DataConnector<StorableInfoTableProps, EnhancedProps | null> {
  stores = [StorableStore];

  enhanceProps(): EnhancedProps | null {
    if (
      !this.props.storableSummaryExtras ||
      !this.props.storableSummaryExtras.ancestralStorableIdsByStorableId
    ) {
      return null;
    }

    const allAncestralStorableIds = this.props.storables.flatMap(
      (storable) => this.props.storableSummaryExtras?.ancestralStorableIdsByStorableId?.[storable.id] ?? []
    );
    const storableOrInaccessibleInfoById = this.getOrFetchMultipleMaybeInaccessibleRequired(
      ManagedModelType.STORABLE_SUMMARY,
      allAncestralStorableIds
    );

    return {
      storableOrInaccessibleInfoById,
    };
  }
}

// If pagination is set, itemWord also needs to be set
type StorableInfoTablePaginationProps =
  | {
      itemWord: string;
      pagination: ListingPagination;
    }
  | {
      itemWord?: string;
      pagination?: null;
    };

interface StorableInfoTableMainProps {
  containableId?: string;
  emptyTableText: string;
  storables: Array<StorableSummary>;
  storableSummaryExtras?: StorableSummaryExtras;
  isLoading?: boolean;
  onChangeArchivePurposes?: (purposes: Array<string>) => void;
  plateSummaryExtras?: PlateSummaryExtras;
  selectedArchivePurposes?: Array<string>;
  setOffset?: (offset: number) => void;
  storableOrInaccessibleInfoById?: {
    [id: string]: StorableSummary | InaccessibleInfo;
  };
  containerContentContainableId?: string;
  dataTestAttrs?: DataTestAttrs;
  displayPrintLabelButton?: boolean;
}

export type StorableInfoTableProps = StorableInfoTableMainProps & StorableInfoTablePaginationProps;

export function StorableInfoTable(props: StorableInfoTableProps): JSX.Element {
  const validContentsInfo = useContainerValidContentsQuery(
    props.storables.filter((storable) => APIUtil.isContainer(storable.id))
  );
  const hasAnyContainers = props.storables.some((storable) => isContainer(storable));

  const shouldShowExpirationColumn =
    hasAnyContainers &&
    BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE &&
    validContentsInfo?.validContentsInfoByContainerId &&
    Object.values(validContentsInfo?.validContentsInfoByContainerId).some(
      (validContentsInfo) => validContentsInfo.expirationInfo?.isTrackingEnabled
    );
  const shouldShowFreezeThawColumn =
    hasAnyContainers &&
    BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW &&
    validContentsInfo?.validContentsInfoByContainerId &&
    Object.values(validContentsInfo?.validContentsInfoByContainerId).some(
      (validContentsInfo) => validContentsInfo.freezeThawInfo?.isTrackingEnabled
    );

  const displayArchivePurposesDropdown = props.onChangeArchivePurposes && props.selectedArchivePurposes;
  const displayPrintLabelButton = props.displayPrintLabelButton;

  return (
    <div {...getTestProps(props.dataTestAttrs)}>
      {(displayArchivePurposesDropdown || displayPrintLabelButton) && (
        <div className="u-flex-display">
          {displayArchivePurposesDropdown && (
            <ArchivePurposeDropdown
              itemIds={props.storables.map((storable) => storable.id)}
              onChangePurposes={props.onChangeArchivePurposes!}
              selectedPurposes={props.selectedArchivePurposes!}
            />
          )}
          {displayPrintLabelButton && (
            <PrintLabelButton
              className={displayArchivePurposesDropdown ? 'u-mini-spacing-left' : undefined}
              buttonStyle={ButtonStyle.PRIMARY}
              itemIds={props.storables.map((storable) => storable.id)}
              shouldAlwaysShowSampleStatusTable={true}
              disabled={props.storables.length === 0}
            />
          )}
        </div>
      )}
      <Table
        emptyTableText={props.emptyTableText}
        message={
          props.pagination &&
          getTableMessage(props.pagination.totalWithHidden, props.pagination.total, props.itemWord)
        }
        numRows={props.storables.length}
        isLoading={props.isLoading}
        pagination={props.pagination}
        setOffset={props.setOffset}
      >
        <Column
          header="Barcode"
          renderCell={(rowIndex) => (
            <LinkCell
              onClick={() => {
                StorableActions.open(props.storables[rowIndex]);
              }}
            >
              {props.storables[rowIndex].barcode}
            </LinkCell>
          )}
        />
        <Column header="Name" renderCell={(rowIndex) => <Cell>{props.storables[rowIndex].name}</Cell>} />
        {props.plateSummaryExtras && props.containableId && (
          <Column
            header="Wells with entity"
            renderCell={(rowIndex) => {
              const storable = props.storables[rowIndex];
              const count = props.plateSummaryExtras!.viewableWellCountByPlateId[storable.id];

              if (!count) {
                return null;
              }

              return (
                <LinkCell
                  onClick={() => {
                    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.CLICKED_INVENTORY_LINK, {
                      actionOrigin: ActionOrigin.REGISTRY_ITEM_PAGE_STORAGE_TAB,
                      containableId: props.containableId,
                      plateId: storable.id,
                      wellsWithEntityCount: count,
                    });

                    navigateToURL(
                      getInventorySearchUrlForContainable({
                        containableId: props.containableId!,
                        registryId: storable.storableSchema.registryId,
                        storableId: storable.id,
                      })
                    );
                  }}
                >
                  {count}
                </LinkCell>
              );
            }}
          />
        )}
        <Column
          header="Created"
          renderCell={(rowIndex) => <Cell>{readableTime(props.storables[rowIndex].createdAt)}</Cell>}
        />
        {props.storableSummaryExtras && props.storableOrInaccessibleInfoById && (
          <Column
            header="Location"
            renderCell={(rowIndex) => (
              <Cell>
                <AncestralStorables
                  noLocationText="None"
                  storableId={props.storables[rowIndex].id}
                  ancestralStorableIds={
                    props.storableSummaryExtras!.ancestralStorableIdsByStorableId?.[
                      props.storables[rowIndex].id
                    ]
                  }
                  storableOrInaccessibleInfoById={props.storableOrInaccessibleInfoById!}
                  gridCell={null}
                  showGridCell={false}
                  truncate={false}
                />
              </Cell>
            )}
          />
        )}
        {props.storableSummaryExtras && props.storableOrInaccessibleInfoById && hasAnyContainers && (
          <Column
            header="Position"
            renderCell={(rowIndex) => {
              const storable = props.storables[rowIndex];
              const gridCell = props.storableSummaryExtras!.gridCellsByContainerId![storable.id];
              if (!storable.parentStorableId || !gridCell) {
                return null;
              }

              const ancestralStorableIds =
                props.storableSummaryExtras!.ancestralStorableIdsByStorableId![props.storables[rowIndex].id];

              if (
                ancestralStorableIds.some((id) =>
                  isInaccessibleInfo(props.storableOrInaccessibleInfoById![id])
                )
              ) {
                return null;
              }

              // Disabling this rule, because filtering with (elem) => isAccessible(elem) removes the type guard
              // eslint-disable-next-line benchlint/no-function-ref-in-array-method
              const ancestralStorables = ancestralStorableIds
                .map((id) => props.storableOrInaccessibleInfoById![id])
                .filter(isAccessible);

              const parentStorable = ParentStorableUtil.getParentStorableFromAncestralStorables(
                storable,
                ancestralStorables.map((s) => restStorableSummaryToGraphQL(s))
              );

              if (!parentStorable) {
                // parentStorable may be null briefly in some situations e.g. when a storable is moved to a new location
                // and the ancestor storables have not been updated yet.
                // In such cases, we're unable to render a proper cell, so we return null.
                // It's expected that these renders will exist only briefly and that some other backend call will
                // fetch the desired storable summary extras with the updated ancestor information.
                return null;
              }

              return (
                <Cell>
                  {SampleUtil.formatGridPosition({
                    // Parent storable is guaranteed to be a grid, since we check for grid cell earlier
                    // in the function and return null if there's no grid cell
                    gridType: APIUtil.apiIdToType(parentStorable.id) as unknown as GridType,
                    rowIndex: gridCell.rowIndex,
                    columnIndex: gridCell.columnIndex,
                    width: (parentStorable as GridSummaryGraphQL).storableSchema.width,
                  })}
                </Cell>
              );
            }}
          />
        )}
        {hasAnyContainers && (
          <Column
            header={getQuantityDisplayString()}
            renderCell={(rowIndex) => {
              const storable = props.storables[rowIndex];
              return storable && isContainer(storable) ? (
                <Cell>
                  <Measurement measurement={storable.quantity} />
                </Cell>
              ) : null;
            }}
          />
        )}
        {hasAnyContainers && props.containerContentContainableId && (
          <Column
            header="Concentration"
            renderCell={(rowIndex) => {
              const storable = props.storables[rowIndex];
              return storable && isContainer(storable) ? (
                <Cell>
                  <ContainerContentConcentration
                    container={storable}
                    storableSummaryExtras={props.storableSummaryExtras}
                    containerContentContainableId={props.containerContentContainableId!}
                  />
                </Cell>
              ) : null;
            }}
          />
        )}
        {shouldShowExpirationColumn && (
          <Column
            header="Expiration date"
            renderCell={(rowIndex) => {
              const expirationInfo: MinimalExpirationInfo | undefined =
                validContentsInfo?.validContentsInfoByContainerId[props.storables[rowIndex]?.id]
                  ?.expirationInfo;
              return expirationInfo ? (
                <Cell>
                  <ExpirationDateCell expirationInfo={expirationInfo} />
                </Cell>
              ) : null;
            }}
          />
        )}
        {shouldShowFreezeThawColumn && (
          <Column
            header="Freeze/thaw count"
            renderCell={(rowIndex) => {
              const freezeThawInfo: MinimalFreezeThawInfo | undefined =
                validContentsInfo?.validContentsInfoByContainerId[props.storables[rowIndex]?.id]
                  ?.freezeThawInfo;
              return freezeThawInfo ? (
                <Cell>
                  <FreezeThawCountCell freezeThawInfo={freezeThawInfo} />
                </Cell>
              ) : null;
            }}
          />
        )}
        {getStorableTagColumns(props.storables)}
      </Table>
    </div>
  );
}

export default StorableInfoTableConnector.connect(StorableInfoTable);
